<script setup lang="ts">
import { computed } from 'vue'
import CollapsibleCard from './CollapsibleCard.vue'
import type { VixTermStructure } from '../../api/client'

const props = defineProps<{
  vix: number | null
  regimeLabel: string
  termStructure: VixTermStructure | null
}>()

const summary = computed(() => {
  const v = props.vix != null ? props.vix.toFixed(1) : '--'
  const struct = props.termStructure ? ` · ${props.termStructure.structure_label}` : ''
  return `${props.regimeLabel} · ${v}${struct}`
})
</script>

<template>
  <CollapsibleCard title="VIX Regime" testid="vix-regime-card" :summary="summary">
    <div class="vix-header">
      <span class="vix-regime-badge" :class="'regime-' + regimeLabel.toLowerCase()">{{ regimeLabel }}</span>
      <span class="vix-spot-val">{{ vix?.toFixed(1) ?? '--' }}</span>
    </div>

    <!--
      Inline VIX term-structure SVG retired 2026-05-03 — replaced
      by the dedicated /charts/vixregime view (TradingView Lightweight
      Charts, full curve + history). The regime badge above + the
      structure label below preserve the at-a-glance view here;
      for the full chart, the operator clicks through to
      /charts/vixregime via the breadcrumbs. Term-structure staleness
      badge is surfaced in the anomaly rail, not duplicated here.
    -->
    <div class="vix-footer">
      <span v-if="termStructure" :class="'ts-' + termStructure.structure">
        {{ termStructure.structure === 'contango' ? '▲' : termStructure.structure === 'backwardation' ? '▼' : '—' }}
        {{ termStructure.structure_label }}
      </span>
      <a href="/charts/vixregime" class="chart-link" title="Open the full VIX regime chart">view chart &rarr;</a>
    </div>
  </CollapsibleCard>
</template>

<style scoped>
.vix-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 4px;
}
.vix-regime-badge {
  font-size: 1.0rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 8px;
  border-radius: 3px;
}
.regime-low { background: rgba(63,185,80,0.15); color: #3fb950; }
.regime-normal { background: rgba(110,118,129,0.15); color: #c9d1d9; }
.regime-high { background: rgba(248,81,73,0.15); color: #f85149; }
.regime-extreme { background: rgba(248,81,73,0.25); color: #f85149; }
.vix-spot-val { font-size: 1.325rem; font-weight: 600; font-family: 'JetBrains Mono', monospace; color: #c9d1d9; }

.vix-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.94rem;
  color: #6e7681;
  margin-top: 3px;
  gap: 8px;
}
.chart-link {
  color: #6a9fff;
  text-decoration: none;
  font-size: 0.8rem;
  opacity: 0.75;
  transition: opacity 0.15s;
}
.chart-link:hover { opacity: 1; }
.ts-contango { color: #3fb950; }
.ts-backwardation { color: #f85149; }
.ts-flat { color: #6e7681; }
</style>
